<template>
  <div class="legal-document-view" :class="{ 'is-embedded': embedded }">
        <HeaderGuest v-if="!embedded && !userStore.isAuthenticated" />
    <div v-if="!embedded" class="back-navigation">
      <button @click="goBack" class="back-btn" type="button">
        <span>←</span>
        <span>{{ t('legalDocuments.backToLegal') }}</span>
      </button>
    </div>
    <div v-if="!embedded" class="document-header">
      <h1 class="document-title">{{ doc.title }}</h1>
      <div class="document-meta">
        <span v-for="item in doc.meta" :key="item" class="meta-item">{{ item }}</span>
      </div>
    </div>
    <LegalProse :sections="doc.sections" />
    <div v-if="!embedded" class="document-signature">
      <div class="signature-info">
        <p><strong>{{ t('legalDocuments.effectiveDate') }}:</strong> {{ doc.effective }}</p>
        <p><strong>{{ t('legalDocuments.documentVersion') }}:</strong> {{ doc.version }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from '@/composables/useI18n';
import HeaderGuest from '@/components/HeaderGuest.vue';
import { useUserStore } from '@/stores/user';
import LegalProse from '@/components/legal/LegalProse.vue';
import { legalDocument } from '@/content/legal';

withDefaults(defineProps<{ embedded?: boolean }>(), {
  embedded: false,
});

const { t, locale } = useI18n();
const doc = computed(() => legalDocument('offer', locale.value));
const router = useRouter();
const userStore = useUserStore();

const goBack = () => {
  router.push('/legal');
};
</script>

<style scoped>
.legal-document-view {
  padding: 2rem;
  max-width: 900px;
  margin: 0 auto;
}

.legal-document-view.is-embedded {
  max-width: none;
  padding: 0;
}

.is-embedded :deep(.content-section) {
  margin-bottom: 2rem;
}

.is-embedded :deep(.content-section:last-child) {
  margin-bottom: 0;
}

.is-embedded .document-signature {
  margin-top: 2rem;
  padding-bottom: 0.5rem;
}

/* Навигация назад */
.back-navigation {
  margin-bottom: 2rem;
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: var(--text-secondary);
  font-size: 1rem;
  cursor: pointer;
  transition: color var(--transition-fast);
}

.back-btn:hover {
  color: var(--text-primary);
}

/* Заголовок документа */
.document-header {
  margin-bottom: 3rem;
  text-align: center;
  padding-bottom: 2rem;
  border-bottom: 1px solid var(--border-primary);
}

.document-title {
  font-size: 2.5rem;
  font-weight: 800;
  margin: 0 0 1rem 0;
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.document-meta {
  display: flex;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.meta-item {
  font-size: 0.875rem;
  color: var(--text-secondary);
  padding: 0.5rem 1rem;
  background: var(--background-tertiary);
  border-radius: var(--radius-md);
  border: 1px solid var(--border-primary);
}

/* Содержание документа */
.document-content {
  line-height: 1.7;
}

.content-section {
  margin-bottom: 2.5rem;
}

.content-section h2 {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 1rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid var(--primary-blue);
}

.content-section p {
  font-size: 1rem;
  color: var(--text-secondary);
  margin: 0 0 1rem 0;
}

.content-section ul {
  margin: 1rem 0;
  padding-left: 1.5rem;
}

.content-section li {
  font-size: 1rem;
  color: var(--text-secondary);
  margin-bottom: 0.5rem;
}

/* Списки определений */
.definition-list {
  display: grid;
  gap: 1rem;
}

.definition-item {
  padding: 1rem;
  background: var(--background-tertiary);
  border-radius: var(--radius-md);
  border: 1px solid var(--border-primary);
}

.definition-item strong {
  color: var(--text-primary);
  display: block;
  margin-bottom: 0.5rem;
}

/* Списки услуг */
.service-list,
.security-list,
.liability-list {
  list-style: none;
  padding: 0;
}

.service-list li,
.security-list li,
.liability-list li {
  padding: 0.5rem 0;
  padding-left: 1.5rem;
  position: relative;
}

.service-list li::before,
.security-list li::before,
.liability-list li::before {
  content: "•";
  color: var(--primary-blue);
  font-weight: bold;
  position: absolute;
  left: 0;
}

/* Контактная информация */
.contact-info {
  background: var(--background-tertiary);
  border-radius: var(--radius-md);
  padding: 1.5rem;
  border: 1px solid var(--border-primary);
}

.contact-item {
  margin-bottom: 0.75rem;
}

.contact-item strong {
  color: var(--text-primary);
}

/* Подпись */
.document-signature {
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid var(--border-primary);
  text-align: center;
}

.signature-info p {
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin: 0.5rem 0;
}

.signature-info strong {
  color: var(--text-primary);
}

/* Адаптивность */
@media (max-width: 768px) {
  .legal-document-view {
    padding: 1rem;
  }
  
  .document-title {
    font-size: 2rem;
  }
  
  .document-meta {
    flex-direction: column;
    align-items: center;
  }
  
  .content-section h2 {
    font-size: 1.25rem;
  }
}
</style> 